import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { setPersistence, browserLocalPersistence, onAuthStateChanged } from 'firebase/auth';
import { Box, CircularProgress } from '@mui/material';

import { auth } from '../firebase';
import NavBar from '../components/NavBar';
import SignIn from '../pages/SignIn';
import Chat from '../pages/Chat';
import Soundboard from '../pages/Soundboard';
import Admin from '../pages/Admin';

// Loading screen component
const LoadingScreen = () => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <CircularProgress size={48} />
  </Box>
);

// Protected route wrapper
const ProtectedRoute = ({ children, user, loading }) => {
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/signin" replace />;
  return children;
};

// Main app content with routing
const AppContent = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
          setUser(currentUser);
          setLoading(false);
        });
        return unsubscribe;
      })
      .catch((error) => {
        console.error('Auth persistence error:', error);
        setLoading(false);
      });
  }, []);

  const showNavBar = user && location.pathname !== '/signin';

  return (
    <Box sx={{
      minHeight: '100vh',
      pb: showNavBar ? { xs: 9, sm: 10 } : 0,
      // Account for safe area on notched devices
      paddingBottom: showNavBar ? 'calc(env(safe-area-inset-bottom, 0px) + 72px)' : 0,
    }}>
      <Routes>
        <Route path="/signin" element={
          user ? <Navigate to="/" replace /> : <SignIn />
        } />
        <Route path="/" element={
          <ProtectedRoute user={user} loading={loading}>
            <Chat />
          </ProtectedRoute>
        } />
        <Route path="/nick" element={
          <ProtectedRoute user={user} loading={loading}>
            <Soundboard character="nick" />
          </ProtectedRoute>
        } />
        <Route path="/stav" element={
          <ProtectedRoute user={user} loading={loading}>
            <Soundboard character="stav" />
          </ProtectedRoute>
        } />
        <Route path="/adam" element={
          <ProtectedRoute user={user} loading={loading}>
            <Soundboard character="adam" />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute user={user} loading={loading}>
            <Admin />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {showNavBar && <NavBar />}
    </Box>
  );
};

// App wrapper with router
const App = () => (
  <BrowserRouter>
    <AppContent />
  </BrowserRouter>
);

export default App;
